import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

// Frueher stand hier ein globaler 401-Handler, der hart auf /login
// umgeleitet hat. Das hat bei abgelaufener Sitzung jede ungespeicherte
// Eingabe vernichtet, teils ohne Zutun im betroffenen Tab (S-1).
//
// Der 401 wird jetzt eine Ebene tiefer behandelt: apiFetch fordert eine
// neue Anmeldung an (ReauthDialog) und wiederholt die Anfrage danach.
// Erreicht ein 401 trotzdem diese Ebene, wurde die Anmeldung abgelehnt —
// dann bleibt der Fehler einfach ein Fehler, ohne die Seite zu verlassen.

/** Wie oft eine Abfrage höchstens nachfasst, wenn Nachfassen Sinn hat. */
const MAX_VERSUCHE = 2;
/** Obergrenze der Wartezeit zwischen zwei Versuchen. */
const MAX_WARTEN_MS = 15_000;

/**
 * Lohnt sich ein zweiter Versuch?
 *
 * Befund 45 (Retry-Sturm, QA-Lauf vom 13.08.2026): im Offline-Zustand
 * liefen in wenigen Minuten rund 989 Anfragen auf — zyklisch auth/me,
 * sessions, bestellungen, settings. Jede fehlgeschlagene Abfrage fasste
 * blind nach, und das Polling schob im Minutentakt neue hinterher. Das
 * ist die Ursache der Endlos-Drehrädchen; verwandt mit dem älteren
 * Befund 35, wo ein 404 sechsmal wiederholt wurde.
 *
 * Die Unterscheidung ist einfach: Der Server hat geantwortet, oder er
 * hat nicht.
 *
 *   4xx  — Er hat geantwortet, und die Antwort ist endgültig. Eine
 *          Bestellung, die es nicht gibt, gibt es beim dritten Mal auch
 *          nicht. Nachfassen ändert nichts und kostet nur Last.
 *   5xx  — Er hat geantwortet, aber die Antwort ist ein Unfall. Ein
 *          überlasteter Prozess kann in zwei Sekunden wieder können.
 *   sonst — Gar keine Antwort: Funkloch, WLAN weg, Server nicht
 *          erreichbar. Genau der Fall, für den es Nachfassen gibt.
 *
 * Bewusst NICHT hier: Offline-Erkennung, Warnbanner, Ersatzseiten. Das
 * ist ein eigenes Arbeitspaket (Befunde 43–47).
 */
function lohntNochmal(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status >= 500;
  }
  return true;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (!lohntNochmal(error)) return false;
        return failureCount < MAX_VERSUCHE;
      },
      // Exponentiell mit Deckel: 1 s, 2 s, 4 s … höchstens 15 s. Bei den
      // derzeit zwei Versuchen wird der Deckel nie erreicht — er steht
      // hier trotzdem, damit ein späteres Heraufsetzen der Versuche kein
      // minutenlanges Warten erzeugt.
      retryDelay: (versuch) => Math.min(1000 * 2 ** versuch, MAX_WARTEN_MS)
    }
  }
});
